import {
  CmpasModule,
  useCommonCmpasModuleRetrieveQuery,
  useCommonCmpasModuleCreateMutation,
  useCommonCmpasModuleUpdateMutation,
  useCommonCmpasModuleSectionListQuery,
  useCommonCmpasModuleSectionUpdateMutation,
} from "@app/api/carApi.generated";
import { useForm } from "./useForm";
import { FormEditorDialog } from "./FormEditorDialog";
import { isDefined } from "@app/utils";
import { usePageTree } from "@app/hooks/usePageTree";
import { useMemo } from "react";

interface ModuleEditorDialogProps {
  id?: string;
  nextSortOrder?: number;
  onApply: () => void;
  onCancel: () => void;
}

interface CmpasModuleWithPages extends CmpasModule {
  pages: string[];
}

export const ModuleEditorDialog = (props: ModuleEditorDialogProps) => {
  const query = useCommonCmpasModuleRetrieveQuery(
    {
      id: props.id ?? "",
    },
    { skip: !props.id },
  );

  const pages = useCommonCmpasModuleSectionListQuery();
  const pageTree = usePageTree();

  const item: CmpasModuleWithPages | undefined = useMemo(
    () =>
      query.data
        ? {
            ...query.data,
            pages:
              pages.data
                ?.filter((p) => p.cmpas_module === props.id)
                .map((p) => p.id)
                .filter(isDefined) ?? [],
          }
        : undefined,
    [query.data, pages.data, props.id],
  );

  const [create] = useCommonCmpasModuleCreateMutation();
  const [update] = useCommonCmpasModuleUpdateMutation();
  const [updatePage] = useCommonCmpasModuleSectionUpdateMutation();

  const updatePages = async ({
    moduleId,
    pageIds,
  }: {
    moduleId?: string;
    pageIds: string[];
  }) => {
    if (!moduleId) {
      return;
    }

    const requests = (pages.data ?? []).map<Promise<any> | undefined>((p) => {
      if (p.cmpas_module !== moduleId && pageIds.some((pid) => pid === p.id)) {
        return updatePage({
          id: p.id ?? "",
          cmpasModuleSectionDetail: { ...p, cmpas_module: moduleId },
        });
      } else if (
        p.cmpas_module === moduleId &&
        !pageIds.some((pid) => pid === p.id)
      ) {
        return updatePage({
          id: p.id ?? "",
          cmpasModuleSectionDetail: { ...p, cmpas_module: undefined as any },
        });
      } else {
        return undefined;
      }
    });

    return Promise.all(requests);
  };

  const form = useForm<CmpasModuleWithPages>({
    entityLabel: "Module",
    getItemName: (item) => `${item.name} (${item.code})`,
    isLoading: query.isLoading || pages.isLoading || pageTree.isLoading,
    item,
    getNewItem: () => ({
      code: "",
      short_id: "",
      name: "",
      is_active: true,
      sort_order: props.nextSortOrder,
      pages: [],
    }),
    onClose: props.onCancel,
    onCreate: async ({ pages, ...item }) => {
      const result = await create({
        cmpasModule: item,
      });

      if (result.data) {
        await updatePages({ moduleId: result.data.id, pageIds: pages });
      }

      return result;
    },

    onUpdate: async ({ pages, ...item }) => {
      const result = await update({
        id: item.id ?? "",
        cmpasModule: item,
      });

      if (result.data) {
        await updatePages({ moduleId: result.data.id, pageIds: pages });
      }

      return result;
    },
    onDelete: async (item) => {
      // todo
    },
    fieldDefs: [
      {
        key: "code",
        type: "string",
        label: "Code",
        description: "Unique code for the module",
        isRequired: true,
      },
      {
        key: "short_id",
        type: "string",
        label: "Short ID",
        description:
          "The short ID for the module to help identify it for pages",
        isRequired: true,
      },
      {
        key: "name",
        type: "string",
        label: "Name",
        description: "Name of the module",
        isRequired: true,
      },
      {
        key: "pages",
        type: "treeselect",
        label: "Page Access",
        description: "The pages this module has access to",
        treeItems: pageTree.items,
      },
      {
        key: "description",
        type: "memo",
        label: "Description",
        description: "Description of the module",
      },
      {
        key: "is_active",
        type: "boolean",
        label: "Is Active",
        description: "Is this module active",
      },
      {
        key: "sort_order",
        type: "integer",
        label: "Sort Order",
        description: "Sort order of module",
      },
    ],
  });

  return <FormEditorDialog form={form} />;
};
